import { cloneDeep, forEach, forOwn, has, isArray, isEmpty, isNil, isObject, isPlainObject, merge } from 'lodash';
import { isEmptyArray } from './lodash';

export const mergeInitialValues = <T extends object>(
  baseDefaultValue: T,
  ...sources: (object | undefined | null)[]
): T => {
  if (isEmptyArray(sources)) return cloneDeep(baseDefaultValue);

  // Deep merge all sources into a single object
  const mergedSource = sources.reduce((acc, source) => {
    if (!source) return acc as T;
    return merge(acc, source) as T;
  }, {} as T) as T;

  // Init results to remove template code
  let result = Object.keys(baseDefaultValue).reduce((acc, key) => {
    const typedKey = key as keyof T;
    const value = baseDefaultValue[typedKey];
    if (isPlainObject(value) && has(value, 'allowUndefined')) {
      // When template or undefined, we set to undefined
      acc[typedKey] = undefined as T[keyof T];
    } else if (isArray(value)) {
      // When array of template item, set to empty array
      acc[typedKey] = [] as T[keyof T];
    } else {
      // When else, we set its value as is
      acc[typedKey] = cloneDeep(value) as T[keyof T];
    }
    return acc;
  }, {} as T);

  // Process the merged source
  Object.entries(mergedSource).forEach(([key, sourceValue]) => {
    const typedKey = key as keyof T;
    const baseValue = baseDefaultValue[typedKey];

    // Do not handle when sourceValue is nil
    if (isNil(sourceValue)) return;

    // Do not handle when key is not present in baseDefaultValue
    if (!(has(baseDefaultValue, typedKey))) return;

    // Handle array
    if (isArray(baseValue)) {

      // Handle when sourceValue is not array
      if (!isArray(sourceValue)) {
        result[typedKey] = undefined as T[keyof T];
        return;
      }

      // Handle when sourceValue is empty array
      if (isEmpty(sourceValue)) {
        result[typedKey] = undefined as T[keyof T];
        return;
      }

      // Handle when baseValue is a template
      if (baseValue.length === 1) {
        const baseTemplate = baseValue[0];
        const mergedArray = sourceValue.map((sourceItem) => {
          if (isPlainObject(sourceItem)) {
            return mergeInitialValues(cloneDeep(baseTemplate) as object, sourceItem);
          }
          return sourceItem;
        });
        result[typedKey] = mergedArray as T[keyof T];
        return;
      }

      // Else
      result[typedKey] = sourceValue as T[keyof T];
      return;
    }

    // Handle object
    if (isPlainObject(baseValue)) {

      // Do not handle when sourceValue is not object
      if (!isPlainObject(sourceValue)) return;

      // Handle when baseValue is a template
      if (has(baseValue, 'allowUndefined')) {
        const { allowUndefined, ...template } = baseValue;
        result[typedKey] = mergeInitialValues(template, sourceValue) as T[keyof T];
        return;
      }

      // Else
      result[typedKey] = mergeInitialValues(baseValue as object, sourceValue) as T[keyof T];
      return;
    }

    // Handle other value types
    result[typedKey] = sourceValue as T[keyof T];
  });

  return result;
};

export const isDisabled = <T,>(values: T, isCustomer: boolean) => {
  const result = {} as Record<keyof T, boolean>;
  forOwn(values, (v, k) => {
    result[k as keyof T] = isCustomer && !!v;
  });
  return result;
}

// Define a type for the data structure
type FormDataValue =
  | string
  | number
  | boolean
  | File
  | null
  | undefined
  | FormDataValue[]
  | { [key: string]: FormDataValue };

// Use const for the function declaration
export const formatFormData = (data: FormDataValue, prefix: string = ''): FormData => {
  const formData = new FormData();

  // Helper function to append data recursively
  const appendData = (obj: FormDataValue, currentKey: string = ''): void => {
    // Handle nil
    if (isNil(obj)) {
      return;
    }

    // Handle File objects
    if (obj instanceof File) {
      formData.append(currentKey, obj);
      return;
    }

    // Handle arrays
    if (isArray(obj)) {
      forEach(obj, (item: FormDataValue) => {
        if (isObject(item) && !(item instanceof File)) {
          // Nested object in array
          forEach(item as Record<string, FormDataValue>, (value: FormDataValue, subKey: string) => {
            appendData(value, `${currentKey}[][${subKey}]`);
          });
        } else {
          // Primitive array value
          formData.append(`${currentKey}[]`, String(item)); // Convert to string for FormData
        }
      });
      return;
    }

    // Handle nested objects
    if (isObject(obj) && !(obj instanceof File)) {
      forEach(obj as Record<string, FormDataValue>, (value: FormDataValue, key: string) => {
        const newKey = currentKey ? `${currentKey}[${key}]` : key;
        appendData(value, newKey);
      });
      return;
    }

    // Handle primitive values (strings, numbers, booleans)
    formData.append(currentKey, String(obj)); // Convert to string for FormData
  };

  // Process the top-level data
  appendData(data, prefix);

  return formData;
};
